import {
  Button,
  Loader,
  Modal,
  ModalContent,
  ModalFooter,
  Offscreen,
  Step,
  Stepper,
  Toast,
  useDidUpdate
} from '@deque/cauldron-react';
import React, {
  type FC,
  type ReactElement,
  useCallback,
  useRef,
  useState
} from 'react';
import styles from './SendToIntegrationModal.css';
import { type TFunction, useTranslation } from 'react-i18next';
import { useSendToIntegrationModal } from '../hooks/useSendToIntegrationModal';
import type { IntegrationData, SendToIntegrationContainerData } from '../types';
import { type SendIssueEventArgs } from '../hooks/useSendToIntegrationLogic';

const getStepStatus = (currentStep: number, stepNumber: number) =>
  currentStep === stepNumber
    ? 'current'
    : currentStep > stepNumber
    ? 'complete'
    : 'future';

const getOffscreenText = (stepStatus: string, t: TFunction) =>
  stepStatus === 'complete' ? (
    <Offscreen>{t('Completed step')}</Offscreen>
  ) : stepStatus === 'future' ? (
    <Offscreen>{t('Future step')}</Offscreen>
  ) : (
    <Offscreen>{t('Current step')}</Offscreen>
  );

export interface Step {
  heading: string;
  content: ReactElement;
  isValid?: () => boolean;
}

export interface SendToIntegrationModalProps {
  closeFn: () => void;
  selectedIntegrationData: IntegrationData;
  sendToIntegrationContainerData: SendToIntegrationContainerData;
  apiHostname: string;
  authFn: () => Promise<string>;
  userEnterpriseId: string;
  onSuccess: (args: SendIssueEventArgs) => void;
  onError?: (args: SendIssueEventArgs) => void;
}

export const SendToIntegrationModal: FC<SendToIntegrationModalProps> = ({
  closeFn,
  ...props
}) => {
  const { t } = useTranslation();

  const headingRef = useRef<HTMLHeadingElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);

  const {
    steps,
    isProjectsLoading,
    onModalSubmit,
    isSaving,
    sendIssueError,
    setSendIssueError,
    connectionIdsWithLoadingErrors
  } = useSendToIntegrationModal({ ...props, closeModal: closeFn });

  const [currentStep, setCurrentStep] = useState(0);

  useDidUpdate(() => {
    headingRef.current?.focus();
  }, [currentStep]);

  useDidUpdate(() => {
    loaderRef.current?.focus();
  }, [isProjectsLoading]);

  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  const incrementStep = useCallback(() => setCurrentStep(s => s + 1), []);
  const onBackClick = useCallback(() => setCurrentStep(s => s - 1), []);
  const onContinueClick = () => {
    const isValid = steps[currentStep].isValid?.();

    if (!isValid) {
      return;
    }

    incrementStep();
  };

  const onSaveClick = async () => {
    const isValid = steps[currentStep].isValid?.();

    if (!isValid) {
      return;
    }

    await onModalSubmit();
  };

  const isLoading = isProjectsLoading || isSaving;

  return (
    <Modal
      className={styles.stepperModal}
      aria-labelledby="stepper-modal-dialog-heading"
      show={true}
      heading={
        <span id="stepper-modal-dialog-heading">
          {t('Share {{ multiple }}to {{ integrationName }}', {
            multiple: props.selectedIntegrationData?.isMultipleIssues
              ? t('All ')
              : '',
            integrationName: props.selectedIntegrationData?.productName
          })}
        </span>
      }
      onClose={closeFn}
    >
      {!!sendIssueError && (
        <Toast
          show
          onDismiss={() => setSendIssueError('')}
          type="error"
          className={styles.chooseTemplateErrorToast}
        >
          {sendIssueError}
        </Toast>
      )}
      {!!connectionIdsWithLoadingErrors.length && isFirstStep && (
        <Toast
          show
          dismissible={false}
          type="caution"
          className={styles.chooseTemplateWarningToast}
        >
          {t('Failed to load connection data for some connections')}
        </Toast>
      )}
      <Stepper>
        {steps.map((step: Step, i: number) => (
          <Step
            key={step.heading}
            status={getStepStatus(currentStep, i)}
            aria-label={step.heading}
          >
            {step.heading} {getOffscreenText(getStepStatus(currentStep, i), t)}
          </Step>
        ))}
      </Stepper>
      <ModalContent>
        {isLoading ? (
          <Loader label={t('Loading...')} ref={loaderRef} />
        ) : (
          <div className={styles.dialogMainContent}>
            <h3 ref={headingRef} tabIndex={-1}>
              {steps[currentStep].heading}
            </h3>
            {steps[currentStep].content}
          </div>
        )}
      </ModalContent>
      <ModalFooter className={styles.modalFooter}>
        {isFirstStep ? (
          <Button variant="secondary" onClick={closeFn}>
            {t('Cancel')}
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={onBackClick}
            disabled={isLoading}
          >
            {t('Back')}
          </Button>
        )}

        {isLastStep ? (
          <Button onClick={onSaveClick} disabled={isLoading}>
            {t('Save')}
          </Button>
        ) : (
          <Button onClick={onContinueClick}>{t('Continue')}</Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
